import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { R } from '@core/utils/r';
import {
  UserCourseStepId,
  UserCourseSectionResponse,
  CourseStepAnswers,
  PreviewCourseResponse,
  PreviewCourseStepResponse,
} from '@business/entities/user_course';
import { map, switchMap } from 'rxjs/operators';
import { CourseSectionId, CourseId, CourseStepResponse } from '@business/entities/course';
import { Observable, of } from 'rxjs';
import { PreviewCourseAPI } from '@business/api/PreviewCourse';
import { CourseAPI } from '@business/api/course';

export class PreviewCourseBloc extends BaseBloc {
  private readonly courseRepo = new Repository<PreviewCourseResponse>({
    getItemId: course => course.id,
  });

  readonly courses$ = this.courseRepo.items$;
  readonly course$ = this.courseRepo.selectItem;

  readonly step$ = (courseId: CourseId, sectionId: CourseSectionId, stepId: UserCourseStepId) => {
    return this.courseRepo.selectItem(courseId).pipe(
      map(course => {
        if (course) {
          const sections = (R.get(course, 'sections') || []) as UserCourseSectionResponse[];
          const section = sections.find(s => s.id === sectionId);
          if (section) {
            return section.steps.find(s => s.id === stepId);
          }
        }
        return null;
      }),
    );
  };

  onReset() {
    this.courseRepo.reset();
  }

  fetchCourse = (id: CourseId) => {
    return PreviewCourseAPI.fetchCourse(id).pipe(
      this.courseRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_templates_error') }),
    );
  };

  stepComplete = (id: CourseId, stepId: UserCourseStepId, answers: CourseStepAnswers) => {
    return PreviewCourseAPI.stepComplete(id, stepId, answers).pipe(
      this.ops.updateStep(),
      handleMessage({ error: requestMessage('fetch_templates_error') }),
    );
  };

  onProgress = (id: CourseId, stepId: UserCourseStepId, progress: CourseStepAnswers) => {
    return PreviewCourseAPI.onProgress(id, stepId, progress).pipe(handleMessage({ error: requestMessage('fetch_templates_error') }));
  };

  updateStep = () => {
    return (source$: Observable<PreviewCourseStepResponse>): Observable<PreviewCourseStepResponse> => {
      return of(null).pipe(
        switchMap(() => source$),
        this.courseRepo.ops.updateOne(newStep => ({
          id: newStep.courseId,
          changes: course => {
            const newSections = course.sections.map(section => {
              if (section.id === newStep.sectionId) {
                const newSteps = section.steps.map(step => {
                  if (step.id === newStep.id) {
                    return newStep;
                  }
                  return step;
                });
                return { ...section, steps: newSteps };
              }
              return section;
            });
            return { sections: newSections };
          },
        })),
      );
    };
  };

  updateCourseStep = (courseId: CourseId, id: number, data: Partial<CourseStepResponse>) => {
    return CourseAPI.updateCourseStep(id, data).pipe(
      switchMap(() => this.fetchCourse(courseId)),
      handleMessage({
        error: requestMessage('update_course_section_step'),
      }),
    );
  };

  ops = { updateStep: this.updateStep };
}
